import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Users, Save, X, FileDown, FileSpreadsheet, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, pln } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const iso = (d) => d.toISOString().slice(0, 10);
const today = () => iso(new Date());
const presets = () => {
  const n = new Date(), y = n.getFullYear(), m = n.getMonth(), q = Math.floor(m / 3);
  return {
    month: { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    quarter: { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) },
    year: { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) },
  };
};

export const WorkOrders = () => {
  const confirm = useConfirm();
  const [workers, setWorkers] = useState([]);
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const [nw, setNw] = useState({ name: "", hourly_rate: "" });
  const [editW, setEditW] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [ne, setNe] = useState({ work_date: today(), hours: "", note: "" });
  const [editE, setEditE] = useState(null);

  const loadWorkers = useCallback(async () => {
    try { const { data } = await api.get("/workers"); setWorkers(data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  const loadEntries = useCallback(async (wid) => {
    if (!wid) return;
    try { const { data } = await api.get(`/workers/${wid}/entries`, { params: { from: from || undefined, to: to || undefined } }); setData(data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [from, to]);
  useEffect(() => { if (sel) loadEntries(sel); }, [sel, loadEntries]);

  const addWorker = async () => {
    if (!nw.name.trim()) return toast.error("Podaj imię i nazwisko");
    try { await api.post("/workers", { name: nw.name.trim(), hourly_rate: parseFloat(nw.hourly_rate) || 0 }); toast.success("Dodano pracownika"); setNw({ name: "", hourly_rate: "" }); loadWorkers(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveWorker = async () => {
    if (!editW.name.trim()) return toast.error("Podaj imię i nazwisko");
    try { await api.put(`/workers/${editW.id}`, { name: editW.name.trim(), hourly_rate: parseFloat(editW.hourly_rate) || 0 }); toast.success("Zapisano zmiany"); setEditW(null); loadWorkers(); if (sel) loadEntries(sel); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delWorker = async (w) => {
    if (!(await confirm({ title: "Usunąć pracownika?", description: `Usunąć „${w.name}" wraz z całym zapisanym czasem pracy?`, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/workers/${w.id}`); toast.success("Pracownik usunięty"); if (sel === w.id) { setSel(null); setData(null); } loadWorkers(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addEntry = async () => {
    if (!ne.work_date) return toast.error("Podaj datę");
    if (!(parseFloat(ne.hours) > 0)) return toast.error("Podaj liczbę godzin (> 0)");
    try { await api.post(`/workers/${sel}/entries`, { work_date: ne.work_date, hours: parseFloat(ne.hours), note: ne.note }); setNe({ work_date: today(), hours: "", note: "" }); loadEntries(sel); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveEntry = async () => {
    try { await api.put(`/work-entries/${editE.id}`, { work_date: editE.work_date, hours: parseFloat(editE.hours) || 0, note: editE.note || "" }); setEditE(null); loadEntries(sel); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delEntry = async (en) => {
    if (!(await confirm({ title: "Usunąć wpis?", description: `Usunąć wpis czasu pracy z dnia ${en.work_date}?`, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/work-entries/${en.id}`); loadEntries(sel); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const download = async (fmt) => {
    if (!sel) return;
    if (!data || data.entries.length === 0) return toast.info("Brak danych do eksportu w wybranym okresie");
    try {
      const res = await api.get(`/workers/${sel}/entries/export.${fmt}`, { params: { from: from || undefined, to: to || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rozliczenie-${(selWorker?.name || "pracownik").replace(/\s+/g, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Nie udało się pobrać pliku"); }
  };

  const toggleSettled = async (en) => {
    const next = !en.settled;
    const ok = await confirm(next
      ? { title: "Oznaczyć jako rozliczone?", description: `Czy na pewno oznaczyć wpis z dnia ${en.work_date} jako rozliczony?`, confirmText: "Rozlicz" }
      : { title: "Cofnąć rozliczenie?", description: `Czy na pewno cofnąć rozliczenie wpisu z dnia ${en.work_date}?`, confirmText: "Cofnij", danger: true });
    if (!ok) return;
    try { await api.put(`/work-entries/${en.id}/settle`, { settled: next }); loadEntries(sel); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const settleAll = async () => {
    const unsettled = (data?.entries || []).filter((e) => !e.settled);
    if (unsettled.length === 0) return toast.info("Brak nierozliczonych wpisów w tym okresie");
    const okrs = (from || to) ? "w wybranym okresie" : "(wszystkie okresy)";
    const ok = await confirm({ title: "Rozliczyć wszystkie?", description: `Czy na pewno oznaczyć wszystkie nierozliczone wpisy ${okrs} (${unsettled.length}) jako rozliczone?`, confirmText: "Rozlicz wszystkie" });
    if (!ok) return;
    try {
      const res = await api.post(`/workers/${sel}/entries/settle-all`, { settled: true }, { params: { from: from || undefined, to: to || undefined } });
      toast.success(`Rozliczono ${res.data.updated} wpis(ów)`);
      loadEntries(sel);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const p = presets();
  const MONTHS_PL = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
  const _now = new Date();
  const fmtPl = (s) => s.split("-").reverse().join(".");
  const lblMonth = MONTHS_PL[_now.getMonth()];
  const lblQuarter = `${fmtPl(p.quarter.from)}–${fmtPl(p.quarter.to)}`;
  const lblYear = String(_now.getFullYear());
  const selWorker = workers.find((w) => w.id === sel);

  return (
    <div className="space-y-5" data-testid="work-orders">
      <div>
        <p className="font-mono text-xs text-amber-400 tracking-widest uppercase">Rozliczenia pracowników</p>
        <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Zlecenia</h2>
        <p className="text-sm text-muted-foreground mt-1">Dodawaj pracowników i ich stawki godzinowe, rejestruj czas pracy i rozliczaj koszt w wybranym okresie.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        {/* Workers column */}
        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid="worker-add-form">
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Nowy pracownik</p>
            <Input data-testid="worker-name-input" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} placeholder="Imię i nazwisko" className="h-9" />
            <div className="flex gap-2">
              <Input data-testid="worker-rate-input" type="number" step="0.01" min="0" value={nw.hourly_rate} onChange={(e) => setNw({ ...nw, hourly_rate: e.target.value })} placeholder="Stawka zł/h" className="h-9" />
              <Button data-testid="worker-add-button" onClick={addWorker} className="h-9 gap-1 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold shrink-0"><Plus className="h-4 w-4" />Dodaj</Button>
            </div>
          </div>

          {workers.length === 0 ? (
            <div data-testid="workers-empty" className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Brak pracowników. Dodaj pierwszego powyżej.</div>
          ) : (
            <div className="space-y-2" data-testid="workers-list">
              {workers.map((w) => (
                <div key={w.id} data-testid={`worker-${w.id}`} className={`rounded-xl border p-3 transition-colors ${sel === w.id ? "border-amber-400 bg-amber-400/5" : "border-border bg-card hover:border-amber-400/50"}`}>
                  {editW?.id === w.id ? (
                    <div className="space-y-2">
                      <Input data-testid={`worker-edit-name-${w.id}`} value={editW.name} onChange={(e) => setEditW({ ...editW, name: e.target.value })} className="h-8 text-sm" />
                      <div className="flex gap-2">
                        <Input data-testid={`worker-edit-rate-${w.id}`} type="number" step="0.01" min="0" value={editW.hourly_rate} onChange={(e) => setEditW({ ...editW, hourly_rate: e.target.value })} className="h-8 text-sm" placeholder="Stawka zł/h" />
                        <Button data-testid={`worker-edit-save-${w.id}`} size="icon" onClick={saveWorker} className="h-8 w-8 bg-amber-400 text-[#0D1117] hover:bg-amber-500 shrink-0"><Save className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditW(null)} className="h-8 w-8 shrink-0"><X className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button data-testid={`worker-select-${w.id}`} onClick={() => setSel(w.id)} className="min-w-0 flex-1 text-left">
                        <p className="font-semibold truncate">{w.name}</p>
                        <p className="text-xs text-muted-foreground">{pln(w.hourly_rate)}/h</p>
                      </button>
                      <Button data-testid={`worker-edit-${w.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditW({ id: w.id, name: w.name, hourly_rate: String(w.hourly_rate ?? "") })}><Pencil className="h-4 w-4" /></Button>
                      <Button data-testid={`worker-delete-${w.id}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400" onClick={() => delWorker(w)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Settlement column */}
        <div>
          {!sel ? (
            <div data-testid="worker-none-selected" className="rounded-xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground h-full flex items-center justify-center">Wybierz pracownika z listy, aby rejestrować i rozliczać czas pracy.</div>
          ) : (
            <div className="space-y-4" data-testid="settlement-panel">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <h3 className="font-display text-xl font-extrabold">{selWorker?.name} <span className="text-sm text-muted-foreground font-normal">• {pln(selWorker?.hourly_rate || 0)}/h</span></h3>
                <div className="flex flex-wrap gap-2">
                  <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-1.5" data-testid="settle-unsettled">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-amber-300/80">Do rozliczenia</p>
                    <p className="font-display text-sm font-extrabold tabular-nums leading-tight"><span data-testid="settle-unsettled-hours">{data?.unsettled_hours ?? 0}</span> h • <span data-testid="settle-unsettled-cost">{pln(data?.unsettled_cost ?? 0)}</span></p>
                  </div>
                  <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5" data-testid="settle-settled">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-emerald-300/80">Rozliczone</p>
                    <p className="font-display text-sm font-extrabold tabular-nums leading-tight"><span data-testid="settle-settled-hours">{data?.settled_hours ?? 0}</span> h • <span data-testid="settle-settled-cost">{pln(data?.settled_cost ?? 0)}</span></p>
                  </div>
                  <div className="rounded-lg bg-secondary px-3 py-1.5" data-testid="settle-total">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Razem</p>
                    <p className="font-display text-sm font-extrabold tabular-nums leading-tight"><span data-testid="settle-total-hours">{data?.total_hours ?? 0}</span> h • <span data-testid="settle-total-cost">{pln(data?.total_cost ?? 0)}</span></p>
                  </div>
                </div>
              </div>

              {/* Date filters */}
              <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card px-4 py-3" data-testid="settle-filters">
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Data od</span>
                  <Input data-testid="settle-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px] h-9" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">do</span>
                  <Input data-testid="settle-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px] h-9" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Skróty</span>
                  <div className="flex flex-wrap gap-1.5">
                    <Button data-testid="settle-preset-month" variant="outline" size="sm" className="h-auto py-1 flex-col leading-tight font-semibold" onClick={() => { setFrom(p.month.from); setTo(p.month.to); }}><span>Bieżący miesiąc</span><span className="text-[10px] font-normal opacity-70">{lblMonth}</span></Button>
                    <Button data-testid="settle-preset-quarter" variant="outline" size="sm" className="h-auto py-1 flex-col leading-tight font-semibold" onClick={() => { setFrom(p.quarter.from); setTo(p.quarter.to); }}><span>Bieżący kwartał</span><span className="text-[10px] font-normal opacity-70">{lblQuarter}</span></Button>
                    <Button data-testid="settle-preset-year" variant="outline" size="sm" className="h-auto py-1 flex-col leading-tight font-semibold" onClick={() => { setFrom(p.year.from); setTo(p.year.to); }}><span>Bieżący rok</span><span className="text-[10px] font-normal opacity-70">{lblYear}</span></Button>
                  </div>
                </div>
                {(from || to) && <Button data-testid="settle-clear" variant="outline" size="sm" className="h-9 font-semibold" onClick={() => { setFrom(""); setTo(""); }}>Wyczyść</Button>}
                <div className="ml-auto flex items-end gap-2">
                  <Button data-testid="settle-all-button" variant="outline" size="sm" className="h-9 gap-1.5 font-semibold border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" onClick={settleAll}><CheckCheck className="h-4 w-4" />Rozlicz wszystkie</Button>
                  <Button data-testid="settle-export-csv" variant="outline" size="sm" className="h-9 gap-1.5 font-semibold" onClick={() => download("csv")}><FileSpreadsheet className="h-4 w-4" />CSV</Button>
                  <Button data-testid="settle-export-pdf" variant="outline" size="sm" className="h-9 gap-1.5 font-semibold" onClick={() => download("pdf")}><FileDown className="h-4 w-4" />PDF</Button>
                </div>
              </div>

              {/* Add entry */}
              <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card px-4 py-3" data-testid="entry-add-form">
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Data</span>
                  <Input data-testid="entry-date-input" type="date" value={ne.work_date} onChange={(e) => setNe({ ...ne, work_date: e.target.value })} className="w-[150px] h-9" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Godziny</span>
                  <Input data-testid="entry-hours-input" type="number" step="0.25" min="0" value={ne.hours} onChange={(e) => setNe({ ...ne, hours: e.target.value })} placeholder="np. 8" className="w-[110px] h-9" />
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Opis (opcjonalnie)</span>
                  <Input data-testid="entry-note-input" value={ne.note} onChange={(e) => setNe({ ...ne, note: e.target.value })} placeholder="np. montaż, szycie…" className="h-9" />
                </div>
                <Button data-testid="entry-add-button" onClick={addEntry} className="h-9 gap-1 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Plus className="h-4 w-4" />Dodaj czas</Button>
              </div>

              {/* Entries table */}
              {!data || data.entries.length === 0 ? (
                <div data-testid="entries-empty" className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Brak zarejestrowanego czasu pracy w wybranym okresie.</div>
              ) : (
                <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="entries-table">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead className="bg-secondary text-muted-foreground font-mono text-[11px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2.5">Data</th>
                        <th className="text-right px-3 py-2.5">Godziny</th>
                        <th className="text-left px-3 py-2.5">Opis</th>
                        <th className="text-right px-3 py-2.5">Koszt</th>
                        <th className="text-center px-3 py-2.5">Rozl.</th>
                        <th className="px-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries.map((en) => (
                        <tr key={en.id} data-testid={`entry-${en.id}`} className={`border-t border-border hover:bg-accent/40 ${en.settled ? "bg-emerald-500/5" : ""}`}>
                          {editE?.id === en.id ? (
                            <>
                              <td className="px-4 py-2"><Input data-testid={`entry-edit-date-${en.id}`} type="date" value={editE.work_date} onChange={(e) => setEditE({ ...editE, work_date: e.target.value })} className="h-8 text-xs w-[140px]" /></td>
                              <td className="px-3 py-2"><Input data-testid={`entry-edit-hours-${en.id}`} type="number" step="0.25" min="0" value={editE.hours} onChange={(e) => setEditE({ ...editE, hours: e.target.value })} className="h-8 text-xs w-[80px] ml-auto" /></td>
                              <td className="px-3 py-2"><Input data-testid={`entry-edit-note-${en.id}`} value={editE.note} onChange={(e) => setEditE({ ...editE, note: e.target.value })} className="h-8 text-xs" /></td>
                              <td className="px-3 py-2 text-right text-muted-foreground">—</td>
                              <td className="px-3 py-2 text-center text-muted-foreground">—</td>
                              <td className="px-3 py-2"><div className="flex justify-end gap-1">
                                <Button data-testid={`entry-edit-save-${en.id}`} size="icon" onClick={saveEntry} className="h-8 w-8 bg-amber-400 text-[#0D1117] hover:bg-amber-500"><Save className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" onClick={() => setEditE(null)} className="h-8 w-8"><X className="h-4 w-4" /></Button>
                              </div></td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-2.5 font-medium tabular-nums">{en.work_date}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{en.hours}</td>
                              <td className="px-3 py-2.5 text-muted-foreground">{en.note || "—"}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums font-bold text-amber-300">{pln(en.cost)}</td>
                              <td className="px-3 py-2.5 text-center"><Checkbox data-testid={`entry-settle-${en.id}`} checked={!!en.settled} onCheckedChange={() => toggleSettled(en)} /></td>
                              <td className="px-3 py-2.5"><div className="flex justify-end gap-1">
                                <Button data-testid={`entry-edit-${en.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditE({ id: en.id, work_date: en.work_date, hours: String(en.hours), note: en.note || "" })}><Pencil className="h-4 w-4" /></Button>
                                <Button data-testid={`entry-delete-${en.id}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400" onClick={() => delEntry(en)}><Trash2 className="h-4 w-4" /></Button>
                              </div></td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border bg-secondary/40 font-bold">
                        <td className="px-4 py-2.5">Razem</td>
                        <td className="px-3 py-2.5 text-right tabular-nums" data-testid="entries-sum-hours">{data.total_hours}</td>
                        <td />
                        <td className="px-3 py-2.5 text-right tabular-nums text-amber-300" data-testid="entries-sum-cost">{pln(data.total_cost)}</td>
                        <td />
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
